import BaseFilterResponse from './base-filter-response';
import MathUtils from '../utils/math';
import FilterConstants from '../../filter-constants';
import * as StageUtils from '../utils/stage';

export default class Chebyshev extends BaseFilterResponse {
  name = FilterConstants.responseType.chebyshev;

  /**
    Chebyshev Algorithm
    @function Chebyshev_lowpass - This is a Chebyshev filter algorithm.
    @param {int}  passbandAttenuation - passband attenuation from the user in dB
    @param {int}  stopbandAttenuation - stopband attenuation from the user in dB
    @param {int}  passbandFrequency - passband frequency from the user in Hz
    @param {int}  stopbandFrequency - stopband frequency from the user in Hz
    @param {int}  passbandRipple - passband ripple from the user in dB
    @description This function calculates the chebyshev filter response
    @returns {array} stageObjects array of stageObjects containing order,fp,Q,fz, and gain properties
    */
  lowPass(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency, passbandRipple) {
    //get fK
    var rippleX = Math.pow(10, 0.1 * passbandRipple) - 1;
    var { fripple_k, fK } = this._computefk(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      passbandRipple,
    );

    //loop t}hrough fK get closest index >= passbandFrequency
    var orderNum = this._getClosestNum(passbandFrequency, fK);

    //get a
    var a = this._calculateA(orderNum, rippleX);

    //build magK array
    var magK = this._magK(a, orderNum);

    var fp_nominal = fripple_k[orderNum - 1][1] * (passbandFrequency / fK[orderNum - 1]);
    //calculate q_K, fp_k build array
    var qKfp = this._calculateQ(magK, orderNum, fp_nominal);

    var stageValues = [];
    if (orderNum <= FilterConstants.maxOrder) {
      //build array of objects
      var loopCount = Math.floor(orderNum / 2);

      var stageId = 1;

      var m = Math.floor(orderNum / 2);

      //check if the order is odd
      if (orderNum % 2 !== 0) {
        var sqrtTwobyTwo = Math.sqrt(2) / 2;
        var firstOrder = {
          stageQ: sqrtTwobyTwo,
          stageFp: qKfp[m][1],
          stageFz: 1,
          stageId: stageId,
          orderType: 1,
          gain: 1,
        };
        stageValues.push(firstOrder);
        stageId++;
      }

      if (orderNum % 2 !== 0) {
        m = m + 1;
      }

      for (var k = 0; k < loopCount; k++) {
        var secondOrder = {
          stageQ: qKfp[m][0],
          stageFp: qKfp[m][1],
          stageFz: 1,
          stageId: stageId,
          orderType: 2,
          gain: 1,
        };
        stageValues.push(secondOrder);
        stageId++;
        m++;
      }
    }

    var result = StageUtils.createTargetStages(stageValues);
    return result;
  }

  getOrder(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency, passbandRipple) {
    var _computefk = this._computefk(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      passbandRipple,
    );
    //loop through fK get closest index >= passbandFrequency
    var result = this._getClosestNum(passbandFrequency, _computefk.fK);
    return result;
  }

  /**
    @function getClosestValue
    @param {int} passbandFrequency - passbandFrequency - passband frequency from the user in db
    @param {array} fKArray - array of fK values
    @description this function gets the closest arrayIndex >=passbandFrequency
    @returns {int} nearestIndex- closest index >= passbandFrequency
    */
  _getClosestNum(passbandFrequency, fKArray) {
    var nearestIndex = null,
      neardiff = null;

    for (var x = 0; x < fKArray.length; x++) {
      if (fKArray[x] >= passbandFrequency) {
        var diff = Math.abs(fKArray[x] - passbandFrequency);

        if (diff <= neardiff || neardiff === null) {
          neardiff = diff;
          nearestIndex = x + 1;
        }
      }
    }

    return nearestIndex;
  }

  _computefk(negPassbandAttenuation, negStopbandAttenuation, passbandFrequency, stopbandFrequency, passbandRipple) {
    //need positive numbers for formula to work (turn negatives to positive)
    const passbandAttenuation = negPassbandAttenuation * -1;
    const stopbandAttenuation = negStopbandAttenuation * -1;

    var rippleX = Math.pow(10, 0.1 * passbandRipple) - 1;
    var stopX_odd = Math.pow(10, 0.1 * stopbandAttenuation) - 1;
    var passX_odd = Math.pow(10, 0.1 * passbandAttenuation) - 1;
    var stopX_even = Math.pow(10, 0.1 * (stopbandAttenuation + passbandRipple)) - 1;
    var passX_even = Math.pow(10, 0.1 * (passbandAttenuation + passbandRipple)) - 1;

    //get S_odd
    var s_odd = Math.sqrt(stopX_odd / rippleX);
    s_odd = MathUtils.aCosh(s_odd);

    //get S_even
    var s_even = Math.sqrt(stopX_even / rippleX);
    s_even = MathUtils.aCosh(s_even);

    //get P_odd
    var p_odd = Math.sqrt(passX_odd / rippleX);
    p_odd = MathUtils.aCosh(p_odd);

    //get P_even
    var p_even = Math.sqrt(passX_even / rippleX);
    p_even = MathUtils.aCosh(p_even);

    //get fripple_k
    var fripple_k = this._fRippleK(stopbandFrequency, s_odd, s_even);

    //get fK
    var result = this._fK(fripple_k, p_odd, p_even, passbandFrequency);
    return { fripple_k: fripple_k, fK: result };
  }

  /**
    @function fK
    @param {array} fripple_k -array of values as calculated in _fRippleK function
    @returns {array} f array
    @description this function loops through fRipple_k array performs calculations and outputs an fK array
    */
  _fK(fripple_k, p_odd, p_even) {
    var result = [],
      stepOne,
      stepTwo;

    for (var k = 0; k <= 19; k++) {
      //get fripple_k * this.get('math').cosh(p/k)
      if (fripple_k[k][0] % 2 === 0) {
        stepOne = MathUtils.cosh(p_even / fripple_k[k][0]);
        stepTwo = fripple_k[k][1] * stepOne;
      }
      if (fripple_k[k][0] % 2 !== 0) {
        stepOne = MathUtils.cosh(p_odd / fripple_k[k][0]);
        stepTwo = fripple_k[k][1] * stepOne;
      }

      result.push(stepTwo);
    }

    return result;
  }

  /**
    @function _fRippleK
    @param {int}  stopbandFrequency - stopband frequency from the user in Hz
    @returns {array} [k, stopbandFrequency/cosh(s/k)]
    @description this function calculates fripple_k
    */
  _fRippleK(stopbandFrequency, s_odd, s_even) {
    var result = [];
    var coshSK;
    var stepTwo;
    for (var k = 1; k <= 20; k++) {
      //if k is even
      if (k % 2 === 0) {
        coshSK = MathUtils.cosh(s_even / k);
        stepTwo = stopbandFrequency / coshSK;
      }
      if (k % 2 !== 0) {
        coshSK = MathUtils.cosh(s_odd / k);
        stepTwo = stopbandFrequency / coshSK;
      }

      result.push([k, stepTwo]);
    }

    return result;
  }

  /**
    @function magK
    @param {int} a value calculated from _calculateA function
    @param {int} n orderNum
    @returns {array} magK [sqRt(realK^2 + imagK^2),realK] result
    @description This function calculates realK (-sinh a sin (180(2k-1)/2n), imagK (cosh a cos (180(2k-1)/2n) and magK (sqrt(realK2+imagK2)
    */
  _magK(a, n) {
    var result = [];

    for (var k = 1; k <= n; k++) {
      var stepOne = 2 * k - 1;
      var stepTwo = (stepOne / (2 * n)) * Math.PI;

      //stepTwo = stepTwo * (Math.PI / 180);
      //get realK
      var sinThree = Math.sin(stepTwo);
      var sinhFive = -1 * MathUtils.sinh(a) * sinThree;
      var realKsqr = sinhFive * sinhFive;

      //get imagK
      var imagKThree = Math.cos(stepTwo);
      var imagK = MathUtils.cosh(a) * imagKThree;
      var imagKsqr = imagK * imagK;

      result.push([Math.sqrt(realKsqr + imagKsqr), sinhFive]);
    }

    return result;
  }

  /**
    @function _calculateA
    @param {int} n index number closest to passbandFrequency
    @param {int} rippleX passband ripple supplied by user
    @returns {int} result of 1/n asinh(1/sqRt(rippleX)
    */
  _calculateA(n, rippleX) {
    var a = MathUtils.asinh(1 / Math.sqrt(rippleX));
    a = (1 / n) * a;

    return a;
  }

  /**
    @function _calculateQ
    @param {array} magK result of magK function
    @param {array} realK result of realK function
    @param {int} n index number closest to passbandFrequency
    @returns {array} [qK, fp_k] result
    */
  _calculateQ(magK, n, fp_nominal) {
    var result = [];

    for (var k = 0; k < magK.length; k++) {
      var qK = magK[k][0] / (-2 * magK[k][1]);

      var fp_K = magK[k][0] * fp_nominal;

      result.push([qK, fp_K]);
    }

    return result;
  }
}
